import { Form } from 'antd';
import { apiV1 } from '../../utils/apiClient';
import {
  planFeaturesSummary,
  PriceLookupKey,
  SubscriptionInputValues,
  SubscriptionPlan
} from '@next-is-free-monorepo/next-is-free-types';
import { useMutation } from '@tanstack/react-query';
import { NButton, NTag, type NButtonProps } from '@next-is-free-monorepo/nif-web-components';
import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkBadgeIcon } from '@hugeicons/core-free-icons';
import { useState } from 'react';
import React from 'react';


const SubscriptionPlanSelector = ({
                                    onSelect,
                                    loading = false,
                                    disabledPrices
                                  }: {
  onSelect: (priceLookupKey: PriceLookupKey, plan: SubscriptionPlan) => void
  loading?: boolean,
  disabledPrices?: PriceLookupKey[]
}) => {

  const [selectedInterval, setSelectedInterval] = useState<'yearly' | 'monthly'>('yearly');

  const PRICES: Record<string, Record<'monthly' | 'yearly', {
    price: string,
    priceDescription: string,
    priceLookupKey: PriceLookupKey
  }>> = {
    starter: {
      monthly: { price: '14', priceDescription: '/mese + iva', priceLookupKey: 'starter_monthly' },
      yearly: { price: '9', priceDescription: '/mese + iva (fatturato annualmente)', priceLookupKey: 'starter_yearly' }
    },
    essential: {
      monthly: { price: '35', priceDescription: '/mese + iva', priceLookupKey: 'essential_monthly' },
      yearly: {
        price: '25',
        priceDescription: '/mese + iva (fatturato annualmente)',
        priceLookupKey: 'essential_yearly'
      }
    },
    advanced: {
      monthly: { price: '55', priceDescription: '/mese + iva', priceLookupKey: 'advanced_monthly' },
      yearly: {
        price: '45',
        priceDescription: '/mese + iva (fatturato annualmente)',
        priceLookupKey: 'advanced_yearly'
      }
    }
  };

  const plans = ['Inizio', 'Professionale', 'Avanzato', 'Enterprise'];

  const sections = [
    {
      title: 'Generale',
      features: [
        { name: 'Clienti inclusi', values: ['100', '1000', 'Illimitati', 'Illimitati'] },
        { name: 'Traguardi', values: ['2', '4', '20', '20'] },
        { name: 'Punti vendita collegabili', values: ['1', '3', '10', 'Illimitati'] },
        { name: 'Supporto all’avvio e guida iniziale', values: [false, true, true, true] }
      ]
    },
    {
      title: 'Personalizzazione',
      features: [
        { name: 'Logo e colori del tuo brand', values: [true, true, true, true] },
        { name: 'Collegamenti rapidi (link veloci)', values: [false, true, true, true] }
      ]
    },
    {
      title: 'Analitiche',
      features: [
        { name: 'Statistiche di base', values: [true, true, true, true] },
        { name: 'Statistiche per punto vendita', values: [false, true, true, true] },
     /*   { name: 'Statistiche per gruppi di clienti', values: [false, false, true, true] }*/
      ]
    },
    {
      title: 'Gestione Clienti',
      features: [
        { name: 'Elenco clienti', values: [false, false, true, true] },
        { name: 'Creazione gruppi manuali', values: [false, false, true, true] },
        { name: 'Gruppi automatici intelligenti', values: [false, false, true, true] },
        { name: 'Storico acquisti e transazioni', values: [false, false, true, true] },
        { name: 'Gestione punti da remoto', values: [false, false, true, true] }
      ]
    },
    {
      title: 'Notifiche',
      features: [
        { name: 'Invii mensili inclusi', values: ['Illimitati', 'Illimitati', 'Illimitati', 'Illimitati'] },
        { name: 'Buoni sconto', values: [true, true, true, true] },
        { name: 'Programmazione notifiche', values: [false, true, true, true] },
        { name: 'Call to Action nei messaggi', values: [false, true, true, true] },
        { name: 'Notifiche a gruppi di clienti', values: [false, false, true, true] },
        { name: 'Notifiche personalizzate per cliente', values: [false, false, true, true] },
      /*  { name: 'Video nelle notifiche', values: [false, false, true, true] }*/
      ]
    },
    {
      title: 'Gestione Staff',
      features: [
        { name: 'Utenti staff inclusi', values: ['1', '5', 'Illimitati', 'Illimitati'] },
     /*   { name: 'Gestione permessi avanzata', values: [false, false, true, true] }*/
      ]
    },
    {
      title: 'Integrazioni',
      features: [
        { name: 'Accesso API', values: [false, false, true, true] },
        { name: 'Integrazioni personalizzate', values: [false, false, false, true] }
      ]
    },
    {
      title: 'Enterprise',
      features: [
        { name: 'Supporto dedicato prioritario', values: [false, false, false, true] },
    /*    { name: 'Gestione multi-account', values: [false, false, false, true] },*/
        { name: 'Onboarding 1:1 personalizzato + SLA garantito', values: [false, false, false, true] }
      ]
    }
  ];


  const renderValue = (value: string | boolean) => {
    if (typeof value === 'string') return <span>{value}</span>;
    return value ? (
      <span className="text-green-600">✔</span>
    ) : (
      <span className="text-gray-400">✘</span>
    );
  };


  return (

    <div className={'mt-8 max-w-7xl mx-auto'}>
      <div className={'flex flex-col gap-4 items-center justify-center'}>
        <p className={'font-semibold text-center text-3xl'}>Un piano per tutti i gusti</p>
        <div className={'flex gap-2 items-center justify-center bg-slate-100 p-2 rounded-full'}>
          <button onClick={() => setSelectedInterval('yearly')}
                  className={`py-2 px-4 font-medium ${selectedInterval === 'yearly' && 'bg-primary'} rounded-full`}>
            Annuale (Risparmia fino al 20%)
          </button>
          <button onClick={() => setSelectedInterval('monthly')}
                  className={`py-2 px-4 font-medium ${selectedInterval === 'monthly' && 'bg-primary'} rounded-full`}>
            Mensile
          </button>
        </div>
      </div>
      <div className={'grid grid-cols-1 gap-2 mt-8 md:grid-cols-2 lg:grid-cols-4'}>
        <PlanCard
          onSelect={onSelect}
          subscriptionPlan={'starter'}
          priceLookupKey={PRICES.starter[selectedInterval].priceLookupKey}
          loading={loading}
          buttonProps={{disabled: disabledPrices?.includes(PRICES.starter[selectedInterval].priceLookupKey)}}
          name={'Starter'}
          price={PRICES.starter[selectedInterval].price}
          subtitle={'Per partire in piccolo'}
          priceDescription={PRICES.starter[selectedInterval].priceDescription}
          features={planFeaturesSummary['starter']}
        />
        <PlanCard
          onSelect={onSelect}
          subscriptionPlan={'essential'}
          priceLookupKey={PRICES.essential[selectedInterval].priceLookupKey}
          buttonProps={{disabled: disabledPrices?.includes(PRICES.essential[selectedInterval].priceLookupKey)}}
          loading={loading}
          name={'Basic'}
          price={PRICES.essential[selectedInterval].price}
          subtitle={'Tutto ciò che serve per iniziare davvero'}
          priceDescription={PRICES.essential[selectedInterval].priceDescription}
          features={planFeaturesSummary['essential']}
        />

        <PlanCard
          onSelect={onSelect}
          subscriptionPlan={'advanced'}
          priceLookupKey={PRICES.advanced[selectedInterval].priceLookupKey}
          loading={loading}
          name={'Pro'}
          className={'border-indigo-300 shadow-indigo-100'}
          buttonProps={{disabled: disabledPrices?.includes(PRICES.advanced[selectedInterval].priceLookupKey)}}
          subtitle={'Strumenti avanzati per crescere'}
          priceDescription={PRICES.advanced[selectedInterval].priceDescription}
          price={PRICES.advanced[selectedInterval].price}
          features={planFeaturesSummary['advanced']}
          tag={'Più Scelto'}
        />

        <PlanCard
          onSelect={() => {
          }}
          subscriptionPlan={'enterprise'}
          priceLookupKey={'enterprise_yearly'}
          loading={loading}
          buttonProps={{ children: 'Contattaci' }}
          subtitle={'Soluzioni su misura per grandi aziende'}
          priceDescription={'Prezzo su richiesta'}
          price={'Custom'}
          hideEuro
          name={'Enterprise'}
          features={planFeaturesSummary['enterprise']}
        />

      </div>
      <p className={'text-center w-full text-lg my-10 block'}>Compara tutte le differenze</p>
      <div className="w-full">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
            <tr>
              <th className="text-left py-3 px-4">Funzionalità</th>
              {plans.map((plan) => (
                <th key={plan} className="py-3 px-4 text-center font-semibold">
                  {plan}
                </th>
              ))}
            </tr>
            </thead>
            <tbody>
            {sections.map((section) => (
              <React.Fragment key={section.title}>
                {/* Section header */}
                <tr className="bg-gray-50">
                  <td
                    colSpan={plans.length + 1}
                    className="py-2 px-4 text-sm font-semibold text-gray-700"
                  >
                    {section.title}
                  </td>
                </tr>
                {section.features.map((feature) => (
                  <tr key={feature.name} className="border-t">
                    <td className="py-3 px-4">{feature.name}</td>
                    {feature.values.map((val, i) => (
                      <td key={i} className="py-3 px-4 text-center">
                        {renderValue(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
            </tbody>
          </table>
        </div>

        {/* Mobile grouped by feature */}
        <div className="md:hidden space-y-8">
          {sections.map((section) => (
            <div key={section.title}>
              <h2 className="text-base font-semibold mb-3 text-gray-800">
                {section.title}
              </h2>
              <div className="space-y-6">
                {section.features.map((feature) => (
                  <div
                    key={feature.name}
                    className="border rounded-lg p-4 shadow-sm"
                  >
                    <h3 className="text-sm font-semibold mb-3">{feature.name}</h3>
                    <ul className="space-y-2">
                      {plans.map((plan, i) => (
                        <li
                          key={plan}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-gray-700 font-medium">{plan}</span>
                          {renderValue(feature.values[i])}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
export default SubscriptionPlanSelector;

function PlanCard({
                    name,
                    subtitle,
                    tag,
                    features,
                    price,
                    priceDescription,
                    className,
                    buttonProps,
                    hideEuro,
                    priceLookupKey,
                    subscriptionPlan,
                    loading = false,
                    onSelect
                  }: {
  name: string,
  subtitle: string,
  tag?: string,
  price: string,
  features: string[],
  className?: string,
  priceDescription: string,
  buttonProps?: NButtonProps,
  hideEuro?: boolean,
  priceLookupKey: PriceLookupKey,
  subscriptionPlan: SubscriptionPlan,
  loading?: boolean,
  onSelect: (priceLookupKey: PriceLookupKey, plan: SubscriptionPlan) => void,
}) {
  return (
    <div className={`border p-6 rounded-2xl shadow-lg flex-1 ${className}`}>
      <div className={'flex gap-2 items-center'}>
        <p className={'text-lg font-bold'}>{name}</p>
        {tag &&
          <NTag color={'info'}>{tag}</NTag>
        }
      </div>
      <p className={'text-slate-500 font-medium  h-10'}>{subtitle}</p>
      <p className={'text-4xl font-semibold mt-4'}>{price}<span className={`text-lg ${hideEuro && 'hidden'}`}>€</span>
      </p>
      <p className={'text-slate-500 text-sm h-10'}>{priceDescription}</p>
      <NButton onClick={() => onSelect(priceLookupKey, subscriptionPlan)} loading={loading}
               disabled={buttonProps?.disabled} color={buttonProps?.color ?? 'secondary'}
               className={buttonProps?.className ?? 'w-full my-4'}>{buttonProps?.children ?? 'Seleziona'}</NButton>
      <div>
        {features.map((item) => (
          <div key={item} className={'flex gap-2 mb-2'}>
            <HugeiconsIcon size={15} icon={CheckmarkBadgeIcon} className={'shrink-0 mt-1'} />
            <p>{item}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
